import React, { Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { useDeviceOptimization } from '../../hooks/useDeviceOptimization';

// Lazy load heavy components for better performance
const LazySchoolRegistrationWizard = lazy(() => import('../landingpage/SchoolRegistrationWizard'));
const LazyContactForm = lazy(() => import('../landingpage/ContactForm'));
const LazyFocusAura = lazy(() => import('../focus/FocusAura'));

// Loading spinner component
const ComponentLoader = ({ size = 'md', className = '' }) => {
  const { animationConfig } = useDeviceOptimization();
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <motion.div
        animate={animationConfig.enabled ? { rotate: 360 } : {}}
        transition={animationConfig.enabled ? { 
          duration: 1, 
          repeat: Infinity, 
          ease: "linear" 
        } : {}}
        className={`border-2 border-primary/30 border-t-primary rounded-full ${sizeClasses[size]}`}
      />
    </div>
  );
};

// Wrapper for lazy components with error boundary
const LazyComponentWrapper = ({ children, fallback = <ComponentLoader />, minLoadTime = 200 }) => {
  return (
    <Suspense 
      fallback={
        <div className="min-h-[200px] flex items-center justify-center">
          {fallback}
        </div>
      }
    >
      {children}
    </Suspense>
  );
};

// Error boundary for lazy components
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[200px] flex items-center justify-center">
          <div className="text-center p-6">
            <p className="text-muted-foreground mb-4">Entschuldigung, es gab einen Fehler beim Laden der Komponente.</p>
            <button 
              onClick={() => window.location.reload()}
              className="min-h-[44px] px-4 py-2 bg-cta text-cta-foreground rounded-xl font-semibold hover:bg-cta-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Optimized lazy component exports
export const OptimizedSchoolRegistrationWizard = (props) => (
  <LazyErrorBoundary>
    <LazyComponentWrapper>
      <LazySchoolRegistrationWizard {...props} />
    </LazyComponentWrapper>
  </LazyErrorBoundary>
);

export const OptimizedContactForm = (props) => (
  <LazyErrorBoundary>
    <LazyComponentWrapper>
      <LazyContactForm {...props} />
    </LazyComponentWrapper>
  </LazyErrorBoundary>
);

export const OptimizedFocusAura = (props) => (
  <LazyErrorBoundary>
    <LazyComponentWrapper minLoadTime={100}>
      <LazyFocusAura {...props} />
    </LazyComponentWrapper>
  </LazyErrorBoundary>
);

// Performance monitoring utility
export const usePerformanceMonitor = () => {
  React.useEffect(() => {
    // Monitor performance and log metrics
    if ('performance' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            console.log('Page Load Performance:', {
              loadTime: entry.loadEventEnd - entry.loadEventStart,
              domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
              timeToFirstByte: entry.responseStart - entry.requestStart
            });
          }
          
          if (entry.entryType === 'largest-contentful-paint') {
            console.log('LCP:', entry.startTime);
          }
          
          if (entry.entryType === 'first-input-delay') {
            console.log('FID:', entry.processingStart - entry.startTime);
          }
        }
      });

      observer.observe({ entryTypes: ['navigation', 'largest-contentful-paint', 'first-input-delay'] });

      return () => observer.disconnect();
    }
  }, []);
};

export { ComponentLoader, LazyComponentWrapper, LazyErrorBoundary };
